import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "./lib/GameSnacks";

import App from "./service/App";
import AudioManager, { track_id } from "./service/AudioManager";
import ServiceLocator from "./ServiceLocator";
import GameState from "./state/GameState";
import PreloaderState from "./state/PreloaderState";
import SavedData from "./service/SavedData";
import State from "./lib/State";

import { Signal } from "signals";
import DummyGameSnacks from "./lib/DummyGameSnacks";
import Session, { game_type } from "./Session";


declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class Main
{
    // #region Attributes //

    // private:

    private _app: App = null;

    private _tickCount: number = null;
    private _elapsedTimeForFPS: number = null;
    private _lastTimeScale: number = null;
    private _lastInactivityTime: number = null;

    private _curState: State = null;

    private _isPrintFPS: boolean = null;

    private _onGlobalMuteUpdated: Signal = null;

    private readonly _kMaxElapsedTime: number = 1/30;

    // #endregion //


    public get onGlobalMuteUpdated(): Signal { return this._onGlobalMuteUpdated; }


    // #region Methods //

    public constructor() 
    {
        Howler.autoSuspend = false;

        this._tickCount = 0;
        this._elapsedTimeForFPS = 0;
        //const kUrlParams = new URLSearchParams( window.location.search );
        this._isPrintFPS = false;//!kUrlParams.has( "isPromo" );

        document.head.insertAdjacentHTML( "beforeend", "<style>"
            + "@font-face {"
                + "font-family: 'asap_regular';"
                + "src: url('resources/fonts/Asap_SemiCondensed-Regular.woff2') format('woff2'),"
                    + "url('resources/fonts/Asap_SemiCondensed-Regular.woff') format('woff');"
            + "}"
            + "@font-face {"
                + "font-family: 'asap_bold';"
                + "src: url('resources/fonts/Asap_SemiCondensed-Bold.woff2') format('woff2'),"
                    + "url('resources/fonts/Asap_SemiCondensed-Bold.woff') format('woff');"
            + "}"
            + "@font-face {"
                + "font-family: 'asap_extrabold';"
                + "src: url('resources/fonts/Asap_SemiCondensed-ExtraBold.woff2') format('woff2'),"
                    + "url('resources/fonts/Asap_SemiCondensed-ExtraBold.woff') format('woff');"
            + "}"
            + "</style>" );

        PIXI.Loader.shared
            .add( "game_logo", "resources/img/preloader/title@2x.png" )
            .add( "bg", "resources/img/preloader/bg@2x.png" )
            .add( "loading_bar_front", "resources/img/preloader/loading_bar_front@2x.png" )
            .add( "loading_bar_base", "resources/img/preloader/loading_bar_base@2x.png" )
            .add( "loading_bar", "resources/img/preloader/loading_bar@2x.png" );
        /*for ( let i: number = 0; i < 6; ++i )
        {
            PIXI.Loader.shared.add( "spark_0" + i.toString(), "resources/img/gameplay/color_selection/spark_0" + ( i + 1 ).toString() + "@2x.png" )
        }*/
        PIXI.Loader.shared.onComplete.once( this._onPreloaderAssets_Loaded, this );
        PIXI.Loader.shared.load();
    }

        private _onPreloaderAssets_Loaded(): void
        {
            this._onGlobalMuteUpdated = new Signal();

            this._app = new App();
            this._app.init();

            ServiceLocator.main = this;
            ServiceLocator.app = this._app;
            ServiceLocator.audio = new AudioManager();

            if ( window.GameSnacks )
            {
                this._onGlobalMute_Updated( window.GameSnacks.audio.isEnabled() );
                window.GameSnacks.audio.subscribe( this._onGlobalMute_Updated.bind( this ) );
            }
            else
            {
                // TODO: RELEASE:
                console.error( "Game Snacks object not found." );
                return;

                let dgs: DummyGameSnacks = new DummyGameSnacks();
                window.GameSnacks = dgs;
                ServiceLocator.app.onDummyGameSnacks( dgs );

                this._onGlobalMute_Updated( window.GameSnacks.audio.isEnabled() );
                window.GameSnacks.audio.subscribe( this._onGlobalMute_Updated.bind( this ) );
            }

            ServiceLocator.resources = {
                bg: PIXI.Loader.shared.resources.bg,
                game_logo: PIXI.Loader.shared.resources.game_logo/*,
                spark_00: PIXI.Loader.shared.resources.spark_00,
                spark_01: PIXI.Loader.shared.resources.spark_01,
                spark_02: PIXI.Loader.shared.resources.spark_02,
                spark_03: PIXI.Loader.shared.resources.spark_03,
                spark_04: PIXI.Loader.shared.resources.spark_04,
                spark_05: PIXI.Loader.shared.resources.spark_05*/ };
            ServiceLocator.saved = new SavedData();
            ServiceLocator.saved.load();

            ServiceLocator.audio.changeVolume( 0.6, track_id.MUSIC );
            ServiceLocator.audio.changeVolume( 1, track_id.SFX );
            ServiceLocator.audio.changeVolume( 0.6, track_id.AMBIENT );

            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteMusic, track_id.MUSIC );
            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteSfx, track_id.SFX );
            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteSfx, track_id.AMBIENT );

            /*if ( window.GameSnacks ) 
            {
                window.GameSnacks.game.onPause( this._onGame_Paused.bind( this ) );
                window.GameSnacks.game.onResume( this._onGame_Resumed.bind( this ) );
            }
            else
            {*/
            window.GameSnacks.game.onResume( () => {
                ServiceLocator.audio.resume( track_id.MUSIC );
                ServiceLocator.audio.resume( track_id.SFX );
                ServiceLocator.audio.resume( track_id.AMBIENT );

                gsap.globalTimeline.timeScale( this._lastTimeScale !== null ? this._lastTimeScale : 1 );

                if ( this._lastInactivityTime !== null && Session.i.isGameStarted )
                {
                    let awayTime: number = ( Date.now() - this._lastInactivityTime ) / 1000;
                    if ( awayTime > 20 
                        && Session.i.gameType == game_type.MATCHMAKING
                        && !ServiceLocator.saved.data.isFirstGame )
                    {
                        Session.i.isLeaveGame = true;
                    }
                    else
                    {
                        while ( awayTime > 0 )
                        {
                            let step: number = 0.05;
                            if ( awayTime < step )
                            {
                                step = awayTime;
                            }
                            awayTime -= step;
                            gsap.globalTimeline.time( gsap.globalTimeline.time() + step, false );
                            this._onTick( null, step * 1000 );
                        }
                    }
                }
            } );

            window.GameSnacks.game.onPause( () => {
                ServiceLocator.audio.pause( track_id.MUSIC );
                ServiceLocator.audio.pause( track_id.SFX );
                ServiceLocator.audio.pause( track_id.AMBIENT );

                this._lastTimeScale = gsap.globalTimeline.timeScale();
                gsap.globalTimeline.timeScale( 0 );

                this._lastInactivityTime = Date.now();
            } );
            //}

            this._app.gameDiv.style.backgroundImage = "none";
            this._app.gameDiv.style.backgroundSize = "auto 100%";
            this._app.gameDiv.style.backgroundPosition = "center";
            this._app.gameDiv.style.backgroundRepeat = "none";
            
            this._curState = this._createPreloaderState();

            window.GameSnacks.game.firstFrameReady();

            gsap.ticker.add( this._onTick.bind( this ) );
        }

            private _createPreloaderState(): PreloaderState
            {
                let preloaderState: PreloaderState = new PreloaderState();
                preloaderState.init();
                preloaderState.onEnter();
                preloaderState.onComplete.addOnce( this._onPreloader_Complete, this );
                return preloaderState;
            }

                private _onPreloader_Complete( gameLogoWrapper: PIXI.Container ): void 
                {
                    gameLogoWrapper.parent.removeChild( gameLogoWrapper );

                    this._curState.onLeave();
                    this._curState.end();

                    let gameState: GameState = new GameState();
                    gameState.init();
                    gameState.onEnter( { gameLogoWrapper: gameLogoWrapper });
                    this._curState = gameState;
                    //
                    ServiceLocator.game = gameState;
                }

            private _onGlobalMute_Updated( isAudioEnabled: boolean ): void
            {
                /*if ( !ServiceLocator.game 
                    || ServiceLocator.game && !ServiceLocator.game.ad.isShowingAd )
                {*/
                    Howler.mute( !isAudioEnabled );
                //}

                this._onGlobalMuteUpdated.dispatch( isAudioEnabled );
            }

            private _onTick( time: number, deltaTime: number ): void
            {
                let elapsedTimeInSeconds: number = deltaTime * 0.001;
                if ( elapsedTimeInSeconds > this._kMaxElapsedTime )
                {
                    elapsedTimeInSeconds = this._kMaxElapsedTime;
                }
                this._curState.onUpdate( elapsedTimeInSeconds );

                this._tickCount += 1;
                this._elapsedTimeForFPS += elapsedTimeInSeconds;
                if ( this._elapsedTimeForFPS >= 1 )
                {            
                    if ( this._isPrintFPS )
                    {    
                        this._app.printFPS( this._tickCount );
                    }

                    this._tickCount = 0;
                    this._elapsedTimeForFPS = 0;
                }

                this._app.render();
            }

    // #endregion //
}

// When the index page has finished loading, create our app.
window.onload = () =>
{
    var main: Main = new Main();
}